import { useEffect, useRef, useState } from 'react';

const STATS = [
  { value: 500, suffix: '+', label: 'Filings Completed' },
  { value: 10, suffix: '+', label: 'Years Combined Experience' },
  { value: 98, suffix: '%', label: 'On-Time Filing Rate' },
];

function useCountUp(target: number, isVisible: boolean) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const duration = 1200;
    const start = performance.now();
    let frame: number;
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      setValue(Math.round(target * progress));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, isVisible]);
  return value;
}

export function StatsCounter() {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // No IntersectionObserver (SSR/prerender environment, or a very old
      // browser) — show the final numbers immediately rather than leaving
      // them stuck at zero with no way to trigger the animation.
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-seal-blue py-14">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 text-center sm:grid-cols-3 sm:px-6">
        {STATS.map((stat) => (
          <StatItem key={stat.label} stat={stat} isVisible={isVisible} />
        ))}
      </div>
    </section>
  );
}

function StatItem({ stat, isVisible }: { stat: (typeof STATS)[number]; isVisible: boolean }) {
  const count = useCountUp(stat.value, isVisible);
  return (
    <div>
      <p className="font-display text-4xl font-bold text-paper">
        {count}
        {stat.suffix}
      </p>
      <p className="mt-1 text-sm text-paper/70">{stat.label}</p>
    </div>
  );
}
